import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { defer, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ServiceaftaleWithUpcomingChangesDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-with-upcoming-changes-dto';
import { UpdateMultiplePartnerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-multiple-partner-request';
import { PartnerService, ServiceAftaleService } from 'src/app/api/services';
import { PartnerChangeData, PartnerChangeDialogData } from 'src/app/components/aftale/models';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';

import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { EditMultiplePartnerDialogComponent } from './edit-multiple-partner-dialog/edit-multiple-partner-dialog.component';
import { ExcelDocumentTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-type-enum';
import { ExcelDocumentHandlerTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-handler-type-enum';
import { PartnerWithCustomerCountDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-with-customer-count-dto';
import { ServiceaftalePartnerChangeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-partner-change-dto';
import { NotificationService } from 'src/app/notifications/services/notification.service';

@Component({
  selector: 'app-partner-change',
  templateUrl: './partner-change.component.html',
  styleUrls: ['./partner-change.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PartnerChangeComponent implements OnInit {
  constructor(
    private router: Router,
    private partnerService: PartnerService,
    private serviceAftaleService: ServiceAftaleService,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private notifications: NotificationService,
  ) {}

  get notChanged(): Array<ServiceaftalePartnerChangeDto> {
    return this.serviceaftalerNotChanged;
  }

  get notChangedCount(): number {
    return this.partner.partnerCustomerCount.numberOfAgreements - this.partner.partnerCustomerCount.numberOfAgreementsWithFutureChange;
  }

  get alreadyChanged(): Array<ServiceaftalePartnerChangeDto> {
    return this.serviceaftalerSwitchingToOtherPartner;
  }

  get alreadyChangedCount(): number {
    return this.partner.partnerCustomerCount.numberOfAgreementsWithFutureChange;
  }

  get handlerType(): ExcelDocumentHandlerTypeEnum {
    switch (this.filter) {
      case PartnerChangeFilter.Current:
        return ExcelDocumentHandlerTypeEnum.Current;
      case PartnerChangeFilter.Future:
        return ExcelDocumentHandlerTypeEnum.Future;
      case PartnerChangeFilter.AlreadyChanged:
        return ExcelDocumentHandlerTypeEnum.Changed;
      case PartnerChangeFilter.NotChanged:
        return ExcelDocumentHandlerTypeEnum.Missing;
    }
  }

  partnerNo: number;
  partner: PartnerWithCustomerCountDto;
  serviceaftaler: ServiceaftalePartnerChangeDto[] = [];
  serviceaftalerNotChanged: ServiceaftalePartnerChangeDto[] = [];
  serviceaftalerSwitchingToOtherPartner: ServiceaftalePartnerChangeDto[] = [];
  futureServiceaftaler: ServiceaftalePartnerChangeDto[] = [];
  filteredAftaler: ServiceaftalePartnerChangeDto[] = [];
  moreAgreementsThanShown = 0;
  serviceaftaleCount: number;
  futureServiceaftaleCount: number;

  public PartnerChangeFilter = PartnerChangeFilter;
  filter: PartnerChangeFilter = PartnerChangeFilter.Current;

  aftalerToBeMoved: Map<number, ServiceaftalePartnerChangeDto> = new Map<number, ServiceaftalePartnerChangeDto>();

  displayedColumns: string[] = [
    'actions',
    'Serviceabonnement.Produkt',
    'OkKundeAftaleNr',
    'kundeNavn',
    'adresse',
    'newPartner',
    'changeDate',
  ];

  dataSource: MatTableDataSource<ServiceaftaleWithUpcomingChangesDto>;
  displayMaxRows = 150;
  unsubscribe$ = new Subject<void>();

  getPartner$ = defer(() => {
    this.loader.show();
    return this.partnerService
      .getPartnerWithCustomerCount({
        caller: EGIS_SERVICE_CALLER,
        partnerNo: this.partnerNo,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  getServiceaftaler$ = defer(() => {
    this.loader.show();

    return this.partnerService
      .activeServiceaftaler({
        caller: EGIS_SERVICE_CALLER,
        partnerNo: this.partnerNo,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  getServiceaftalerSwitchingToOtherPartner$ = defer(() => {
    this.loader.show();

    return this.partnerService
      .serviceaftalerSwitchingToOtherPartner({
        caller: EGIS_SERVICE_CALLER,
        partnerNo: this.partnerNo,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  getServiceaftalerNotChanged$ = defer(() => {
    this.loader.show();

    return this.partnerService
      .serviceaftalerNotChanged({
        caller: EGIS_SERVICE_CALLER,
        partnerNo: this.partnerNo,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  getFutureServiceaftaler$ = defer(() => {
    this.loader.show();
    return this.partnerService
      .serviceaftalerSwitchingToPartner({
        caller: EGIS_SERVICE_CALLER,
        partnerNo: this.partnerNo,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  documentType = ExcelDocumentTypeEnum.Partneraftaler;

  ngOnInit(): void {
    this.partnerNo = Number(this.route.snapshot.paramMap.get('partnerNo'));
    this.getPartner$.subscribe(
      (partner) => {
        this.partner = partner;
      },
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );

    this.getServiceaftaler$.subscribe(
      (serviceaftaleResponse) => {
        this.moreAgreementsThanShown =
          serviceaftaleResponse.serviceaftalePartnerChangeCount > this.displayMaxRows
            ? serviceaftaleResponse.serviceaftalePartnerChangeCount
            : 0;
        this.serviceaftaleCount = serviceaftaleResponse.serviceaftalePartnerChangeCount;
        this.serviceaftaler = serviceaftaleResponse.serviceaftalePartnerChangeList;
        this.filteredAftaler = this.serviceaftaler;
        this.dataSource = new MatTableDataSource(this.filteredAftaler);
      },
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );

    this.getFutureServiceaftaler$.subscribe(
      (serviceaftaleResponse) => {
        this.futureServiceaftaleCount = serviceaftaleResponse.serviceaftalePartnerChangeCount;
        this.futureServiceaftaler = serviceaftaleResponse.serviceaftalePartnerChangeList;
      },
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );

    this.getServiceaftalerNotChanged$.subscribe({
      next: (response) => {
        this.serviceaftalerNotChanged = response.serviceaftalePartnerChangeList;
      },
      error: (error) => this.errorHandler.HandleError(error),
    });

    this.getServiceaftalerSwitchingToOtherPartner$.subscribe({
      next: (response) => {
        this.serviceaftalerSwitchingToOtherPartner = response.serviceaftalePartnerChangeList;
      },
      error: (error) => this.errorHandler.HandleError(error),
    });
  }

  changePartner() {
    const data: PartnerChangeDialogData = {
      currentPartnerNo: this.partner.partnerNo,
    };
    const dialogRef = this.dialog.open(EditMultiplePartnerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe(async (partnerChangeData: PartnerChangeData) => {
      if (!partnerChangeData) {
        return;
      }
      const request: UpdateMultiplePartnerRequest = {
        fromPartnerNo: this.partnerNo,
        toPartnerNo: partnerChangeData.partnerNo,
        validFrom: partnerChangeData.validFrom.toOkDateString(),
        okKundeAftaleNrs: Array.from(this.aftalerToBeMoved.keys()),
      };

      this.loader.show();
      this.serviceAftaleService
        .updateMultiplePartner({
          caller: EGIS_SERVICE_CALLER,
          body: request,
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          (updatedAftaler) => {
            this.notifications.success('Partnerskift foretaget på: ' + updatedAftaler.updated.length + ' aftaler');
            updatedAftaler.updated.forEach((x) => {
              const index = this.serviceaftaler.findIndex((sa) => sa.okKundeAftaleNr === x.okKundeAftaleNr);
              if (index >= 0) {
                this.serviceaftaler[index] = x;
              }
            });
            this.partner.partnerCustomerCount = updatedAftaler.partnerCustomerCount;
            this.filteredAftaler = this.serviceaftaler;
            this.aftalerToBeMoved.clear();
            this.filterAgreements(this.filter);
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    });
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  concatAdresseLokation(serviceaftale: ServiceaftaleDto): string {
    const adresse = serviceaftale.kunde?.anlaegsadresse?.adressLine;
    const lokation = serviceaftale.kunde?.lokation;

    if (adresse && lokation) {
      return `${adresse}\u00A0\u00B7\u00A0${lokation}`;
    }
    if (adresse && !lokation) {
      return adresse;
    }
    if (!adresse && lokation) {
      return lokation;
    }
    return 'N/A';
  }

  onServiceaftaleChecked(checked: boolean, serviceaftale: ServiceaftaleDto) {
    if (checked) {
      this.aftalerToBeMoved.set(serviceaftale.okKundeAftaleNr, serviceaftale);
    } else {
      this.aftalerToBeMoved.delete(serviceaftale.okKundeAftaleNr);
    }
  }

  onCheckAll(checked: boolean) {
    this.aftalerToBeMoved.clear();
    if (checked) {
      this.filteredAftaler.forEach((aftale) => {
        this.aftalerToBeMoved.set(aftale.okKundeAftaleNr, aftale);
      });
    }
  }

  onCloseClick() {
    this.router.navigate(['/indstillinger']);
  }

  filterAgreements(stateFilter: PartnerChangeFilter) {
    switch (stateFilter) {
      case PartnerChangeFilter.Current: {
        this.filteredAftaler = this.serviceaftaler.slice(0, 150);
        this.dataSource = new MatTableDataSource(this.filteredAftaler);
        break;
      }
      case PartnerChangeFilter.Future: {
        this.filteredAftaler = this.futureServiceaftaler;
        this.dataSource = new MatTableDataSource(this.filteredAftaler);
        break;
      }
      case PartnerChangeFilter.AlreadyChanged: {
        this.filteredAftaler = this.alreadyChanged;
        this.dataSource = new MatTableDataSource(this.filteredAftaler);
        break;
      }
      case PartnerChangeFilter.NotChanged: {
        this.filteredAftaler = this.notChanged;
        this.dataSource = new MatTableDataSource(this.filteredAftaler);
        break;
      }
    }
    this.filter = stateFilter;
  }
}

export enum PartnerChangeFilter {
  Current,
  Future,
  AlreadyChanged,
  NotChanged,
}
